import React, { useEffect, useState } from "react";
import "./chat.css";
import UserGaurd from "../../../helper/AuthGaurd";
import ColorInit from "../../../helper/ColorInit";
import ScrollToTop from "react-scroll-to-top";
import Preloader from "../../../helper/Preloader";
import HeaderTwo from "../../../components/HeaderTwo";
import Breadcrumb from "../../../components/Breadcrumb";
import FooterTwo from "../../../components/FooterTwo";
import BottomFooter from "../../../components/BottomFooter";
import { useSocket } from "../../../context/SocketProvider";
import { useSearchParams } from "react-router-dom";
import { formatImageName, handleError } from "../../../utils/functions";
import axiosInstance from "../../../config/axios.config";
import { toast } from "react-toastify";
import timeAgo from "../../../utils/timeago";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const Chat = () => {
  const { user } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [message, setMessage] = useState("");

  const chatId = searchParams.get("chatId");

  const socket = useSocket();

  const { t } = useTranslation();

  useEffect(() => {
    const getChatList = async () => {
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.get(`/user/chat`);
        if (!data.error) {
          setData(data.data);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    getChatList();
  }, []);

  useEffect(() => {
    if (!socket) return;

    if (chatId) {
      socket.emit("joinChat", { chatId });

      socket.on("newMessage", (messages) => {
        setActiveChat((prev) => ({
          ...prev,
          messages: messages,
        }));
      });
    }

    setActiveChat(data.find((chat) => chat._id === chatId));

    return () => {
      socket.emit("leaveChat", { chatId });
    };
  }, [chatId, socket, data]);

  const handleActiveChat = (id, chat) => {
    if (chatId === id) {
      setSearchParams({});
      setActiveChat(null);
    } else {
      setSearchParams({ chatId: id });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      if (!message) return;
      const { data } = await axiosInstance.put(`/user/chat/message/${chatId}`, { content: message });
      setMessage("");
      if (data.error) {
        toast.error(data.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <UserGaurd>
      <ColorInit color={true} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#FA6400" />

      {/* Preloader */}
      <Preloader isLoading={isLoading} />

      {/* HeaderTwo */}
      <HeaderTwo category={true} />

      {/* Breadcrumb */}
      <Breadcrumb title={"Chat"} />
      <div className="container mt-20">
        <div className="chat-container">
          {/* Left Sidebar */}
          <div className="chat-sidebar">
            <div className="sidebar-header">
              <div className="user-profile">
                <img
                  src="/assets/images/user.jpg"
                  alt="Profile"
                  className="avatar"
                />
                <div className="user-status">
                  <h4>{user?.name}</h4>
                  <span className="status online">{t("online")}</span>
                </div>
              </div>
            </div>

            <div className="conversations-list">
              {data.map((item) => (
                <div
                  className={`conversation-item ${chatId === item._id ? "active" : ""}`}
                  onClick={() => handleActiveChat(item._id, item)}
                >
                  <img src={formatImageName(item.shop.shopLogo) || "/assets/images/cover.png"} alt="Shop 2" />
                  <div className="conversation-info">
                    <div className="d-flex justify-content-between">
                      <h4>{item.shop.shopName}</h4>
                      <span className="time">{timeAgo(item.updatedAt)}</span>
                    </div>
                    <p className="mb-0 last-message">{item.lastMessage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Chat Area */}
          {chatId && (
            <div className="chat-main">
              <div className="chat-header">
                <div className="chat-header-info">
                  <img
                    src={formatImageName(activeChat?.shop.shopLogo) || "/assets/images/cover.png"}
                    alt="Shop 1"
                  />
                  <div>
                    <h3>{activeChat?.shop.shopName}</h3>
                  </div>
                </div>
              </div>

              <div className="messages-container">
                {activeChat?.messages.map((message) =>
                  message.sender === "user" ? (
                    <div className="message sent">
                      <div className="message-content">
                        <div className="message-bubble">{message?.content}</div>
                        <div className="message-info">
                          <span className="time text-end">{timeAgo(message?.createdAt)}</span>
                          <span className="status">
                            <i className="fas fa-check-double"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="message received">
                      <img src="/assets/images/shop1.jpg" alt="Shop 1" className="message-avatar" />
                      <div className="message-content">
                        <div className="message-bubble">{message?.content}</div>
                        <div className="message-info">
                          <span className="time">{timeAgo(message?.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="chat-input-area">
                <button className="icon-button">
                  <i className="fas fa-paperclip"></i>
                </button>
                <form className="input-wrapper" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    placeholder={t("typeMessage")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <button className="icon-button emoji-button">
                    <i className="far fa-smile"></i>
                  </button>
                  {/* Fix: Send button now has type="submit" */}
                  <button type="submit" className="send-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
                      <path fill="currentColor" d="M4.4 19.425q-.5.2-.95-.088T3 18.5V14l8-2l-8-2V5.5q0-.55.45-.837t.95-.088l15.4 6.5q.625.275.625.925t-.625.925z"></path>
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          )}

          {!chatId && (
            <div className="chat-main">
              <div className="chat-main-inner">
                <div className="text-center">
                  <img src="/assets/images/grey.png" alt="grey" />
                  <br />
                  <span className="mt-20">{t("selectChatPrompt")}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* FooterTwo */}
      <FooterTwo />

      {/* BottomFooter */}
      <BottomFooter />
    </UserGaurd>
  );
};

export default Chat;
